import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TableModule } from 'src/app/table/table.module';
import { TRANSLATION_MAP } from 'src/app/enums';
import { BehaviorSubject } from 'rxjs';
import { IRowActionGetter, ITableData, ITableSettings } from 'src/app/table/interfaces';
import { SharedService } from '../../shared.service';
import { IAvailableContact, IField } from '../../interfaces';
import { SortDirection } from '../../interfaces';
import { UIParticipant, UIState } from '@azavista/participants/dist/event-app/multiregistration/multiregistration-controller';
import { MultiRegistrationService } from '../multi-registration.service';
import { getExistingMultiRegistrationEmails, mapContactToParticipantAttributes } from '../utils';
import { SearchContactResultItem } from '../types';

@Component({
    selector: 'aza-rws-search-contacts-dialog',
    standalone: true,
    imports: [CommonModule, TranslateModule, TableModule],
    templateUrl: './search-contacts-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchContactsDialogComponent implements OnInit {
    @Input() workflowId: string;
    @Input() subParticipantFields: IField[] = [];
    @Input() subParticipantFieldIds: string[] = [];
    @Input() subParticipantEmailField: IField | undefined;
    @Input() state: UIState;

    @Output() closeDialog = new EventEmitter<void>();
    @Output() saveSelection = new EventEmitter<SearchContactResultItem[]>();

    private multiRegSvc = inject(MultiRegistrationService);

    availableContacts: IAvailableContact[] = [];
    isLoading = false;
    selectedContactIds = new Set<string>();
    contactTableSettings: ITableSettings;
    contactTableData$ = new BehaviorSubject<ITableData<IAvailableContact>>({
        pagedResponse: { limit: 0, object_count: 0, offset: 0 },
        rowObjects: [],
    });

    readonly TRANSLATION_MAP = TRANSLATION_MAP;
    readonly contactTableRowActionGetter = this.getContactTableRowActionGetter();

    constructor(
        private sharedSvc: SharedService,
        private cdr: ChangeDetectorRef,
    ) {
        this.contactTableSettings = this.sharedSvc.createDefaultTableSettings('', SortDirection.asc);
    }

    ngOnInit(): void {
        this.loadContacts();
    }

    private async loadContacts(): Promise<void> {
        if (!this.workflowId) return;
        this.isLoading = true;
        this.selectedContactIds.clear();
        this.cdr.detectChanges();
        try {
            this.availableContacts = await this.multiRegSvc.getAvailableContacts(this.workflowId);
            this.contactTableSettings = this.sharedSvc.createDefaultTableSettings('', SortDirection.asc);
            this.emitContactTableData();
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    onClose(): void {
        this.closeDialog.emit();
    }

    onSave(): void {
        if (!this.hasContactSelection()) return;

        const contactsToAdd = this.availableContacts.filter((c) => this.selectedContactIds.has(c.id));
        const emailFieldId = this.subParticipantEmailField?.id;
        const allowedFieldIds = this.subParticipantFieldIds || [];

        let existingEmails: Set<string> | null = null;
        if (emailFieldId) {
            const emails = getExistingMultiRegistrationEmails(this.state, emailFieldId) || [];
            existingEmails = new Set(emails.filter((email): email is string => !!email));
        }

        const resultItems: SearchContactResultItem[] = [];
        contactsToAdd.forEach((contact) => {
            // Skip adding the contact if their email already exists
            if (emailFieldId && existingEmails) {
                const emailValue = contact[emailFieldId] as string | undefined;
                if (emailValue && existingEmails.has(emailValue)) {
                    return;
                }
                if (emailValue) {
                    existingEmails.add(emailValue);
                }
            }
            resultItems.push({
                contact,
                participantAttributes: mapContactToParticipantAttributes(contact, allowedFieldIds)
            });
        });

        this.saveSelection.emit(resultItems);
        this.closeDialog.emit();
    }

    hasContactSelection(): boolean {
        return this.selectedContactIds.size > 0;
    }

    private isContactEmailAlreadyUsed(contact: IAvailableContact): boolean {
        const emailFieldId = this.subParticipantEmailField?.id;
        if (!emailFieldId) return false;
        const emailValue = contact[emailFieldId] as string | undefined;
        if (!emailValue) return false;
        const emails = getExistingMultiRegistrationEmails(this.state, emailFieldId) || [];
        return emails.includes(emailValue);
    }

    toggleContactSelection(contact: IAvailableContact): void {
        if (this.selectedContactIds.has(contact.id)) {
            this.selectedContactIds.delete(contact.id);
        } else {
            this.selectedContactIds.add(contact.id);
        }
        this.emitContactTableData();
    }

    onPageRequested(settings: ITableSettings): void {
        this.contactTableSettings = settings;
        this.emitContactTableData();
    }

    private emitContactTableData(): void {
        if (!this.subParticipantFields?.length || !this.availableContacts?.length) return;
        const { limit, offset } = this.contactTableSettings.page;
        const total = this.availableContacts.length;
        const rowObjects = this.availableContacts.slice(offset, offset + limit);
        this.contactTableData$.next({
            columns: this.subParticipantFields,
            rowObjects,
            pagedResponse: { limit, object_count: total, offset },
        });
    }

    private getContactTableRowActionGetter(): IRowActionGetter<IAvailableContact> {
        return (contact) => ({
            otherActions: [
                {
                    key: 'toggle-contact-select',
                    label: this.selectedContactIds.has(contact.id) ? TRANSLATION_MAP.REMOVE : TRANSLATION_MAP.ADD,
                    actionFn: () => this.toggleContactSelection(contact),
                    disabledFn: (row) => this.isContactEmailAlreadyUsed(row.object),
                },
            ],
        });
    }
}
